import {
  AuthUser,
} from '@aws-amplify/auth';
import { UseAuthenticator } from '@aws-amplify/ui-react';
import { Box, Button } from '@mui/material';
import * as React from 'react';
import CogNeedAlert from '../../../Common/CogNeedAlert';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import EmptyLayout from '../../../Components/EmptyLayout';
import { AmplifyContext } from './index';
import useAmplifyContextStartup from './useAmplifyContextStartup';

interface AmplifyContextProviderProps {
  signOut: UseAuthenticator['signOut'] | undefined;
  user: AuthUser | undefined;
  children: React.ReactNode;
}

interface BootstrapContentProps {
  children: React.ReactNode;
  ready: boolean;
  bootstrapping: boolean;
  startupError: unknown;
  onRetry(): void;
}

function BootstrapLoading() {
  return <EmptyLayout><Box className="content"><LoadingBackdrop /></Box></EmptyLayout>;
}

function BootstrapError({ onRetry }: { onRetry(): void }) {
  return (
    <EmptyLayout>
      <Box className="content" display="flex" flexDirection="column" gap={2}>
        <CogNeedAlert severity="error">
          Unable to load live page data. Please retry.
        </CogNeedAlert>
        <Button variant="contained" onClick={onRetry}>
          Retry
        </Button>
      </Box>
    </EmptyLayout>
  );
}

function BootstrapContent({
  bootstrapping,
  children,
  onRetry,
  ready,
  startupError,
}: BootstrapContentProps) {
  if (ready) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (bootstrapping || !startupError) {
    return <BootstrapLoading />;
  }

  return <BootstrapError onRetry={onRetry} />;
}

export default function AmplifyContextProvider(props: AmplifyContextProviderProps) {
  const { children, signOut, user } = props;
  const {
    amplifyTokens,
    isBootstrapping,
    isReady,
    retry,
    startupError,
    refreshToken,
  } = useAmplifyContextStartup();

  const value = React.useMemo(() => ({
    signOut,
    user,
    idToken: amplifyTokens?.idToken.toString(),
    idTokenExpiresAt: amplifyTokens?.idToken.payload.exp,
    refreshToken,
  }), [amplifyTokens, signOut, user, refreshToken]);

  return (
    <AmplifyContext.Provider value={value}>
      <BootstrapContent
        bootstrapping={isBootstrapping}
        onRetry={retry}
        ready={isReady}
        startupError={startupError}
      >
        {children}
      </BootstrapContent>
    </AmplifyContext.Provider>
  );
}
